import { DatePickerPopover, flash, FormGroup, Label, Modal, numberFormat, SubmitButton, Textarea } from "@xola/ui-kit";
import { DateFormat, formatDate, FormField, InputField, now } from "common";
import { getPaymentReminder } from "common/src/models/PaymentReminder";
import dayjs, { Dayjs } from "dayjs";
import { FormikValues } from "formik";
import { isEmpty } from "lodash";
import React, { useState } from "react";
import { useMutation } from "react-query";
import {
    EmailMessageTemplate,
    Event,
    MessageTemplate,
    PaymentReminder,
    PaymentReminderType,
    Purchase,
    PurchaseItem,
} from "types";
import { number, object } from "yup";
import { api } from "../../api/api";
import { useInvalidate } from "../../hooks/useInvalidation";
import { getNearestPurchaseItem, isPastBooking } from "../../models/PurchaseItem";
import { NoTranslate } from "../Common/NoTranslate";
import { SecondaryButton } from "../Common/SecondaryButton";
import { TimePickerPopover } from "../Common/TimePickerPopover";
import { XolaFormik } from "../Common/XolaFormik";
import { CurrencyInputField } from "../Field/CurrencyInputField";
import { MessageForm } from "@/components/Common/MessageModal/MessageForm";
import { TemplateSelect } from "@/components/Common/MessageModal/TemplateSelect";
import { useCollection } from "@/hooks/useCollection";

interface FormValues extends PaymentReminder {
    dueAt: number;
    tag: string;
    email?: EmailMessageTemplate | null;
}

interface CollectPurchaseBalanceViaInvoiceModalProps {
    purchase: Purchase;
    event?: Event;
    isOpen: boolean;
    onClose: () => void;
    item: PurchaseItem;
    selectClosestArrivalDate?: boolean;
}

const validationSchema = object({
    amount: number().required().min(0.5, "must be 0.5 at minimum"),
    dueAt: number().min(0, "date should be zero or a positive number"),
});

const getPaymentDueDateBeforeArrival = (purchaseItem: PurchaseItem, dueAt: number) => {
    const arrivalDateTime: Dayjs | null = "arrivalDateTime" in purchaseItem ? purchaseItem.arrivalDateTime : null;
    const arrivalDate = "arrivalDate" in purchaseItem ? purchaseItem.arrivalDate : null;
    if (arrivalDateTime) {
        const dueDateTime = arrivalDateTime.subtract(dueAt, "minutes");
        return formatDate(dueDateTime.toDate(), DateFormat.DATE_LONG);
    }

    return formatDate(dayjs(arrivalDate).subtract(dueAt, "minutes").toDate(), DateFormat.DATE_LONG);
};

export const CollectPurchaseBalanceViaInvoiceModal = ({
    purchase,
    event,
    isOpen,
    onClose,
    item,
    selectClosestArrivalDate,
}: CollectPurchaseBalanceViaInvoiceModalProps) => {
    return (
        <Modal isOpen={isOpen} className="overflow-visible" onClose={onClose}>
            <ModalContent
                purchase={purchase}
                event={event}
                item={item}
                selectClosestArrivalDate={selectClosestArrivalDate}
                onClose={onClose}
            />
        </Modal>
    );
};

interface ModalContentProps {
    purchase: Purchase;
    event?: Event;
    onClose: () => void;
    item: PurchaseItem;
    selectClosestArrivalDate?: boolean;
}

const ModalContent = ({ purchase, event, onClose, item, selectClosestArrivalDate = false }: ModalContentProps) => {
    const invalidate = useInvalidate();
    const { data: templates } = useCollection<MessageTemplate>("messageTemplates");
    const emailTemplates = templates.filter(function (template) {
        return template.email && !isEmpty(template.email.body);
    });
    const [template, setTemplate] = useState<MessageTemplate | null>(null);

    const { mutateAsync: collectPurchaseBalance, isLoading: isSaving } = useMutation(async (values: FormValues) => {
        const payload = {
            type: values.type,
            arrivalOffset: values.dueAt,
            amount: values.amount,
            tag: values.tag,
        };
        if (canEmailTraveler && values.email) {
            const channels = [
                {
                    body: values.email?.body,
                    subject: values.email?.subject,
                    type: "email",
                },
            ];

            Object.assign(payload, { messageTemplate: { channels: channels } });
        } else {
            Object.assign(payload, { comment: values.comment });
        }
        await api.post(`/purchases/${purchase.id}/paymentReminders`, payload);

        handlePaymentSuccess(values.amount ?? 0);
    });

    const handleSubmit = async (values: FormValues) => {
        await collectPurchaseBalance(values);
    };

    const handlePaymentSuccess = (amount: number) => {
        flash.success(`Sent invoice for ${numberFormat(amount, purchase.currency)}`);
        if (!event) {
            invalidate("purchases", purchase.id);
        }

        invalidate("users", purchase.organizer?.id);
        onClose();
    };

    const purchaseItem = selectClosestArrivalDate ? getNearestPurchaseItem(purchase, item) : item;
    const hasArrivalDateTime = "arrivalDateTime" in purchaseItem;
    const invoiceTraveler = getPaymentReminder(purchase, [PaymentReminderType.INVOICE_TRAVELER]);
    const canEmailTraveler = !!purchase.customerEmail;

    const initialValues: FormValues = {
        amount: purchase.balance,
        dueAt: 0,
        tag: "",
        comment: "",
        type: PaymentReminderType.INVOICE_TRAVELER,
    };

    if (hasArrivalDateTime && invoiceTraveler) {
        let invoiceAmount = invoiceTraveler.amount ?? purchase.balance ?? 0;
        if (invoiceTraveler.amount && invoiceTraveler.amount > purchase.balance) {
            invoiceAmount = 0;
        }

        const invoiceDueDate =
            invoiceAmount > 0
                ? formatDate(invoiceTraveler.dueAt, DateFormat.DATE_LONG)
                : formatDate(purchaseItem.arrivalDate, DateFormat.DATE_LONG);
        const daysDiff = hasArrivalDateTime
            ? dayjs(purchaseItem.arrivalDate).diff(dayjs(invoiceDueDate).startOf("day"), "days")
            : 0;
        initialValues.dueAt = daysDiff < 0 ? 0 : dayjs.duration(daysDiff, "days").asMinutes();
    }

    const isItemPastBooking = isPastBooking(item);

    const validate = (values: FormikValues) => {
        let errors = {};

        if (values.email) {
            if (isEmpty(values.email.subject)) {
                Object.assign(errors, { "email.subject": "Subject is required" });
            }

            if (isEmpty(values.email.body)) {
                Object.assign(errors, { "email.body": "Body is required" });
            }
        }

        if (isItemPastBooking) {
            return errors;
        }

        if (hasArrivalDateTime && !isPastBooking(purchaseItem)) {
            const isArrivalOffsetAfterNow = dayjs(getPaymentDueDateBeforeArrival(purchaseItem, values.dueAt)).isBefore(
                dayjs().startOf("day"),
            );

            if (isArrivalOffsetAfterNow) {
                errors = {
                    ...errors,
                    dueAt: `date should be after ${formatDate(
                        dayjs().startOf("day").subtract(1, "days").toDate(),
                        DateFormat.DATE_LONG,
                    )}`,
                };
            }
        }

        return errors;
    };

    return (
        <XolaFormik
            enableReinitialize
            validateOnChange
            initialValues={initialValues}
            validationSchema={validationSchema}
            validate={validate}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit, isValid, setFieldValue, values }) => (
                <>
                    <Modal.Header>Collect Payment via Invoice</Modal.Header>
                    <Modal.Body className="flex space-x-4">
                        <fieldset className="flex-1 space-y-4" disabled={isSaving}>
                            <FormField prevalidate name="amount" label="Amount">
                                <CurrencyInputField />
                            </FormField>
                            {isItemPastBooking ? (
                                <FormField name="dueAt" label="Payment due">
                                    <span>
                                        Overdue <NoTranslate>({formatDate(now(), DateFormat.DATE_LONG)})</NoTranslate>
                                    </span>
                                </FormField>
                            ) : hasArrivalDateTime && purchaseItem.arrivalDateTime.isSameOrAfter(now()) ? (
                                <FormField prevalidate name="dueAt" label="Payment due">
                                    <TimePickerPopover
                                        value={values.dueAt}
                                        isLoading={false}
                                        onApply={(value: number) => {
                                            setFieldValue("dueAt", value);
                                        }}
                                    />{" "}
                                    before arrival
                                    <DatePickerPopover
                                        popoverProps={{ appendTo: document.body, zIndex: 40, placement: "right" }}
                                        value={dayjs(purchaseItem.arrivalDate)
                                            .subtract(values.dueAt, "minutes")
                                            .toDate()}
                                        disabledDays={[
                                            {
                                                after: dayjs(purchaseItem.arrivalDate).toDate(),
                                                before: now().toDate(),
                                            },
                                        ]}
                                        onChange={(value: Date) => {
                                            // Convert both dates to the seller's timezone and get just the date part
                                            // This avoids DST issues by comparing date strings instead of datetime objects
                                            const arrivalDateString = formatDate(
                                                purchaseItem.arrivalDate,
                                                DateFormat.DATE_ISO,
                                                true,
                                            );
                                            const selectedDateString = formatDate(value, DateFormat.DATE_ISO, true);
                                            const diffInDays = dayjs(arrivalDateString).diff(
                                                dayjs(selectedDateString),
                                                "days",
                                            );
                                            setFieldValue("dueAt", diffInDays * 24 * 60);
                                        }}
                                    >
                                        <span>
                                            {" "}
                                            (due on{" "}
                                            <NoTranslate as="strong" className="underline hover:cursor-pointer">
                                                {getPaymentDueDateBeforeArrival(purchaseItem, values.dueAt)}
                                            </NoTranslate>
                                            )
                                        </span>
                                    </DatePickerPopover>
                                </FormField>
                            ) : null}

                            {canEmailTraveler && emailTemplates.length > 0 ? (
                                <FormGroup>
                                    <Label>Load Email Template</Label>
                                    <TemplateSelect
                                        className="flex-1"
                                        value={template}
                                        templates={emailTemplates}
                                        onChange={(template) => {
                                            setTemplate(template);
                                            setFieldValue("email", template?.email);
                                        }}
                                    />
                                </FormGroup>
                            ) : null}

                            {template ? (
                                <MessageForm
                                    template={template}
                                    isEmailAvailable={canEmailTraveler}
                                    canToggleEmailChannel={false}
                                />
                            ) : (
                                <FormField name="comment" label="Message">
                                    <InputField
                                        className="h-28"
                                        as={Textarea}
                                        placeholder="Optional message to include in invoice"
                                    />
                                </FormField>
                            )}

                            <FormField name="tag" label="Tag">
                                <InputField placeholder="Add a tag" />
                            </FormField>
                        </fieldset>
                    </Modal.Body>

                    <Modal.Footer className="flex justify-end space-x-4">
                        <fieldset disabled={isSaving} className="flex space-x-4">
                            <div className="flex space-x-2">
                                <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>

                                <SubmitButton isLoading={isSaving} disabled={!isValid} onClick={handleSubmit}>
                                    Send Invoice
                                </SubmitButton>
                            </div>
                        </fieldset>
                    </Modal.Footer>
                </>
            )}
        </XolaFormik>
    );
};
